/**
 * AvailableTagsGrid Component
 * 
 * Компонент для отображения доступных тегов в виде сетки
 * С чекбоксами для выбора/снятия выбора тегов
 */

import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { TagChip } from './TagChip';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import type { Tag } from '@/integrations/supabase/types';
import { useTranslation } from 'react-i18next';

interface AvailableTagsGridProps {
  availableTags: Tag[];
  selectedTags: Tag[];
  onToggleTag: (tag: Tag) => void;
  onEditTag?: (tag: Tag) => void;
  onRemoveTag?: (tag: Tag) => void;
  isLoading?: boolean;
  searchQuery?: string;
}

export const AvailableTagsGrid: React.FC<AvailableTagsGridProps> = ({
  availableTags,
  selectedTags,
  onToggleTag,
  onEditTag,
  onRemoveTag,
  isLoading = false,
  searchQuery = ""
}) => {
  const { t } = useTranslation();

  // Filter tags based on search query
  const filteredTags = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return availableTags;
    }

    const query = searchQuery.toLowerCase();
    return availableTags.filter(tag =>
      tag.name.toLowerCase().includes(query)
    );
  }, [availableTags, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner message={t('records.tag_management.loading')} />
      </div>
    );
  }

  if (availableTags.length === 0) {
    return (
      <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center">
        <p className="text-sm text-muted-foreground mb-2">
          {t('records.tag_management.no_tags')}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('records.tag_management.create_hint')}
        </p>
      </div>
    );
  }

  if (filteredTags.length === 0 && searchQuery.trim()) {
    return (
      <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center">
        <p className="text-sm text-muted-foreground mb-2">
          {t('records.tag_management.not_found')}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('records.tag_management.not_found_query', { query: searchQuery })}
        </p>
      </div>
    );
  }

  const getTagCheckboxId = (tagId: string) => `tag-checkbox-${tagId}`;

  return (
    <div
      data-testid="available-tags-grid"
      className="border border-border rounded-lg p-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filteredTags.map(tag => {
          const isSelected = selectedTags.some(selectedTag => selectedTag.id === tag.id);
          const checkboxId = getTagCheckboxId(tag.id);

          return (
            <div
              key={tag.id}
              className={`flex items-center space-x-3 p-2 rounded-lg border transition-all cursor-pointer hover:bg-muted/50 ${isSelected
                  ? 'bg-muted border-brand-200 shadow-sm'
                  : 'border-transparent hover:border-border'
                }`}
              onClick={() => onToggleTag(tag)}
            >
              <Checkbox
                id={checkboxId}
                checked={isSelected}
                onChange={() => onToggleTag(tag)}
                className="flex-shrink-0"
              />
              <div className="flex-1 min-w-0 flex items-center">
                <label
                  htmlFor={checkboxId}
                  className="flex-1 min-w-0 cursor-pointer"
                >
                  <TagChip
                    tag={tag}
                    size="sm"
                    className="w-full max-w-none"
                    showContextMenuButton={true}
                    onEdit={onEditTag}
                    onRemove={onRemoveTag}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-xs text-muted-foreground">
        {searchQuery.trim() ? (
          t('records.tag_management.found_count', { count: filteredTags.length, total: availableTags.length })
        ) : (
          t('records.tag_management.total_count', { count: availableTags.length })
        )}
      </div>
    </div>
  );
};